'use client';

import { useEffect, useRef } from 'react';

import { useShellRuntime } from '@/components/shell/shell-runtime-provider';
import { t } from '@/lib/design/tokens';

interface NotificationsPanelProps {
  onClose: () => void;
}

export function NotificationsPanel({ onClose }: NotificationsPanelProps) {
  const { notifications, notificationsError, notificationsLoading } =
    useShellRuntime();
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (!panelRef.current) return;
      if (panelRef.current.contains(event.target as Node)) return;
      onClose();
    };
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    // Defer adding the click handler so the opener click doesn't
    // immediately close the panel.
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onDocClick);
    }, 0);
    document.addEventListener('keydown', onEsc);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [onClose]);

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Notifications"
      style={{
        position: 'fixed',
        top: 56,
        right: 16,
        width: 320,
        background: t.card,
        border: `1px solid ${t.cardBorder}`,
        borderRadius: 12,
        zIndex: 100,
        fontFamily: t.font,
        overflow: 'hidden',
        boxShadow: t.shadowLg,
        maxHeight: 360,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '12px 16px',
          fontSize: 11,
          fontWeight: 600,
          color: t.text1,
          borderBottom: `1px solid ${t.border}`,
        }}
      >
        Notifications
      </div>

      <div style={{ overflow: 'auto' }}>
        {notificationsLoading && (
          <div
            style={{
              padding: '24px 16px',
              fontSize: 11,
              color: t.textM,
              textAlign: 'center',
            }}
          >
            Loading notifications…
          </div>
        )}

        {!notificationsLoading && notificationsError && (
          <div
            style={{
              padding: '24px 16px',
              fontSize: 11,
              color: t.textM,
              textAlign: 'center',
              lineHeight: 1.55,
            }}
          >
            Unable to load notifications right now.
          </div>
        )}

        {!notificationsLoading &&
          !notificationsError &&
          notifications.length === 0 && (
            <div
              style={{
                padding: '24px 16px',
                fontSize: 11,
                color: t.textM,
                textAlign: 'center',
              }}
            >
              No notifications
            </div>
          )}

        {!notificationsLoading &&
          !notificationsError &&
          notifications.map((notification) => (
            <div
              key={notification.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '10px 16px',
                borderBottom: `1px solid ${t.border}`,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: t.text1,
                    lineHeight: 1.4,
                  }}
                >
                  {notification.title}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: t.text2,
                    lineHeight: 1.55,
                    marginTop: 2,
                  }}
                >
                  {notification.message}
                </div>
              </div>
              <span
                style={{
                  fontSize: 9,
                  color: t.textM,
                  flexShrink: 0,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {notification.timeLabel}
              </span>
            </div>
          ))}
      </div>
    </div>
  );
}
